
import React, { useState, useEffect } from "react";
import { ServiceCalendar } from "@/components/service-details/booking/ServiceCalendar";
import { CalendarTabs } from "@/components/service-details/booking/CalendarTabs";
import { useCalendarConfig } from "@/components/service-details/booking/CalendarConfig";
import { useEventHandlers } from "@/components/service-details/booking/EventHandlers";
import { useAuth } from "@/contexts/AuthContext";
import { format } from "date-fns";
import { parseStoredDateTime, formatDateForCalendar } from "@/utils/timeUtils";

interface CalendarSectionProps {
  selectedDate: Date;
  onDateSelect: (date: Date | undefined) => void;
  bookings: any[];
  serviceName: string;
  onViewChange?: (view: string) => void;
  service?: any;
  onBookingSelect?: (booking: any) => void;
  onTimeSlotSelect?: (timeSlot: string) => void;
}

export const CalendarSection = ({
  selectedDate,
  onDateSelect,
  bookings,
  serviceName,
  onViewChange,
  service,
  onBookingSelect,
  onTimeSlotSelect
}: CalendarSectionProps) => {
  const [activeView, setActiveView] = useState<string>("timeGridDay");
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [isBookingDialogOpen, setIsBookingDialogOpen] = useState(false);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState("");
  const [selectedBooking, setSelectedBooking] = useState<any>(null);
  const { user, isAuthenticated } = useAuth();
  
  const {
    getBusinessHoursConfig,
    getToolbarConfig,
    getTimeRange,
    getCalendarViews,
    getCalendarHeight,
    getInitialView
  } = useCalendarConfig({ service });

  // Use proper event handlers for admin dashboard
  const { handleDateClick, handleEventClick } = useEventHandlers({
    isLoggedIn: isAuthenticated,
    setSelectedDate: (date: Date) => onDateSelect(date),
    setSelectedTimeSlot: (timeSlot: string) => {
      setSelectedTimeSlot(timeSlot);
      if (onTimeSlotSelect) {
        onTimeSlotSelect(timeSlot);
      }
    },
    setIsBookingOpen,
    setSelectedBooking: (booking: any) => {
      setSelectedBooking(booking);
      if (onBookingSelect) {
        onBookingSelect(booking);
      }
    },
    setBookingAction: () => {}, // Not used in admin view
    setIsBookingActionOpen: () => {}, // Not used in admin view
    currentUserId: user?.id || null,
    setIsBookingDialogOpen,
    isServiceOwner: true, // Admin dashboard assumes owner access
    isAdmin: true
  });

  useEffect(() => {
    if (onViewChange) {
      onViewChange(activeView);
    }
  }, [activeView, onViewChange]);

  const handleViewMount = (viewInfo: any) => {
    setActiveView(viewInfo.view.type);
    if (onViewChange) {
      onViewChange(viewInfo.view.type);
    }
  };

  const handleViewChange = (view: string) => {
    setActiveView(view);
    if (onViewChange) {
      onViewChange(view);
    }
  };

  // Transform bookings into FullCalendar event objects
  const transformBookingsToEvents = (bookingData: any[]) => {
    return bookingData.map(booking => {
      // Format time range for the title
      const startTime = parseStoredDateTime(booking.booking_date || booking.start_time, booking.start_time_local);
      const endTime = parseStoredDateTime(booking.booking_date || booking.end_time, booking.end_time_local);
      const timeRangeTitle = booking.status === 'travel' ? 
        'TRAVEL' : 
        `${format(startTime, 'h:mm a')} - ${format(endTime, 'h:mm a')}`;
      
      // For month and week views, we'll just use the customer name if available
      // The actual rendering logic is handled in the renderEventContent function 
      // inside ServiceCalendar component
      let title = timeRangeTitle;
      if (booking.customer_name && booking.status !== 'blocked' && booking.status !== 'travel') {
        title = `${timeRangeTitle} - ${booking.customer_name}`;
      }
      
      return {
        id: booking.id.toString(),
        title: title,
        start: formatDateForCalendar(startTime),
        end: formatDateForCalendar(endTime),
        classNames: [`event-${booking.status}`],
        borderColor: 'transparent',
        textColor: '#333333',
        extendedProps: {
          status: booking.status,
          booking: booking
        }
      };
    });
  };

  // Convert bookings to calendar events
  const calendarEvents = transformBookingsToEvents(bookings);

  // Calendar configuration
  const businessHours = getBusinessHoursConfig();
  const calendarConfig = {
    toolbarConfig: getToolbarConfig(),
    businessHours: businessHours,
    timeRange: getTimeRange(),
    calendarViews: getCalendarViews(),
    calendarHeight: getCalendarHeight(),
    initialView: getInitialView()
  };

  return (
    <div className="calendar-container bg-white rounded-lg p-4">
      <h2 className="text-lg font-semibold mb-3">Calendar for {serviceName}</h2>
      
      <CalendarTabs
        activeView={activeView}
        onViewChange={handleViewChange}
      />
      
      <ServiceCalendar
        events={calendarEvents}
        handleDateClick={handleDateClick}
        handleEventClick={handleEventClick}
        activeView={activeView}
        onViewMount={handleViewMount}
        calendarConfig={calendarConfig}
        isManagementView={true}
      />
    </div>
  );
};
